/**
 * BillingUsageRepository.ts
 * Owner 빌링/사용량 관리 Repository
 */

import { API_BASE_URL } from '../../../config/api';

// ============================================================================
// Types & Interfaces
// ============================================================================

export interface SubscriptionInfo {
  plan_name: string;
  plan_description?: string;
  status: 'active' | 'suspended' | 'cancelled';
  start_date: string;
  end_date?: string;
  has_custom_values: boolean;
  monthly_base_fee: number;
  alimtalk_unit_price: number;
  alimtalk_free_quota: number;
  alimtalk_free_quota_full?: number;
  billing_type?: 'prepaid' | 'postpaid';
  price?: number;
}

export interface AlimtalkUsage {
  year_month: string;
  total_sent: number;
  success_count: number;
  failed_count: number;
  by_type: Record<string, { success: number; failed: number }>;
  period_start?: string;
  period_end?: string;
  billing_period?: {
    billing_day: number;
    period_start: string;
    period_end: string;
  };
}

/** 카카오 알림톡 사용료 청구서 한 달치 (실제 청구 근거) */
export interface AlimtalkBill {
  year_month: string;
  sent_count: number;
  failed_count: number;
  unit_price: number;
  free_quota: number;
  billable_count: number;
  /** 공급가액 (부가세 전) */
  supply_amount: number;
  vat_amount: number;
  /** 실제로 내야 하는 금액 (공급가액 + 부가세) */
  total_amount: number;
  /** unbilled=안내 전 · billed=안내함 · paid=입금완료 · waived=면제 */
  status: string;
  billed_at: string | null;
  /** 입금 기한 (YYYY-MM-DD, KST). 안내 전이면 null — 이때는 기한 줄을 숨긴다. */
  due_date: string | null;
  /** 안내한 날로부터 며칠 (현재 7일) */
  due_days: number;
  paid_at: string | null;
  breakdown: Array<{ code: string; label: string; count: number }>;
}

export interface MonthlyBill {
  year_month: string;
  plan_name: string;
  breakdown: {
    monthly_base_fee: number;
    alimtalk: {
      total_sent: number;
      free_quota: number;
      billable_count: number;
      unit_price: number;
      cost: number;
    };
    additional_charges: number;
    charge_details: Array<{
      id: string;
      charge_type: string;
      description?: string;
      amount: number;
      created_at: string;
    }>;
  };
  total: number;
}

export interface MonthlyCharge {
  id: string;
  year_month: string;
  charge_type: string;
  description?: string;
  amount: number;
  created_at: string;
}

export interface CreditBalance {
  balance: number;
  free_quota: number;
  free_used: number;
  free_remaining: number;
  free_month: string | null;
}

export interface CreditTransaction {
  id: string;
  type: 'charge' | 'deduct' | 'refund';
  amount: number;
  balance_after: number;
  description: string;
  created_at: string;
}

export interface ChargeRequest {
  amount: number;
  payment_method: string;
  notes?: string;
}

export interface PaymentRecord {
  id: string;
  organization_id: string;
  year_month: string;
  payment_type: 'bank_transfer' | 'card' | 'credit_offset';
  amount: number;
  notes?: string;
  paid_at: string;
  created_by?: string;
  created_at: string;
}

export interface PaymentSummary {
  year_month: string;
  total_billed: number;
  total_paid: number;
  outstanding: number;
  is_paid: boolean;
  payments: PaymentRecord[];
}

export interface FailedNotificationLog {
  id: string;
  notification_type: string | null;
  status: string | null;
  user_name: string;
  phone: string;
  message: string;
  error_message: string | null;
  sent_at: string | null;
}

export interface OutstandingBalance {
  total_outstanding: number;
  months_with_outstanding: Array<{
    year_month: string;
    billed: number;
    paid: number;
    outstanding: number;
  }>;
}

// ============================================================================
// Repository Class
// ============================================================================

export class BillingUsageRepository {
  private getAuthHeaders() {
    const token = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  private async apiCall<T>(url: string, options: RequestInit = {}): Promise<T> {
    const response = await fetch(`${API_BASE_URL}${url}`, {
      ...options,
      headers: {
        ...this.getAuthHeaders(),
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: '알 수 없는 오류' }));
      throw new Error(errorData.detail || `HTTP ${response.status}`);
    }

    return response.json();
  }

  /**
   * 내 조직의 플랜 구독 정보 조회
   */
  async getMySubscription(): Promise<SubscriptionInfo | null> {
    const result = await this.apiCall<{
      subscription: SubscriptionInfo | null;
    }>('/api/v1/owner/billing/subscription');
    return result.subscription;
  }

  /**
   * 알림톡 사용량 조회
   */
  async getUsage(yearMonth?: string): Promise<AlimtalkUsage> {
    const url = yearMonth
      ? `/api/v1/owner/billing/usage?year_month=${yearMonth}`
      : '/api/v1/owner/billing/usage';

    const result = await this.apiCall<{ usage: AlimtalkUsage }>(url);
    return result.usage;
  }

  /**
   * 이번 달 예상 청구액 조회
   */
  async getCurrentMonthBill(): Promise<MonthlyBill | null> {
    const result = await this.apiCall<{
      bill: MonthlyBill | null;
      is_current_month: boolean;
    }>('/api/v1/owner/billing/current-month');
    return result.bill;
  }

  /**
   * 특정 월 청구액 조회
   */
  async getMonthlyBill(yearMonth: string): Promise<MonthlyBill | null> {
    const result = await this.apiCall<{
      bill: MonthlyBill | null;
      is_current_month: boolean;
    }>(`/api/v1/owner/billing/bill/${yearMonth}`);
    return result.bill;
  }

  /**
   * 우리 매장 카카오 알림톡 사용료 청구 내역.
   *
   * 위 getMonthlyBill 은 '플랜'이 있어야 금액이 나오는데 실제로는 어느 매장에도
   * 플랜이 없어 원장이 자기 사용료를 볼 방법이 없었다. 이건 실제 청구 근거를
   * 그대로 읽으므로 플랜과 무관하게 보인다.
   */
  async getAlimtalkBills(months = 12): Promise<{
    bills: AlimtalkBill[];
    /** 입금 계좌. 서버에 설정돼 있지 않으면 null — 이때는 화면에서 계좌 줄을 숨긴다. */
    paymentAccount: string | null;
  }> {
    const result = await this.apiCall<{
      bills: AlimtalkBill[];
      payment_account: string | null;
    }>(`/api/v1/owner/billing/alimtalk-bills?months=${months}`);
    return { bills: result.bills || [], paymentAccount: result.payment_account || null };
  }

  /**
   * 입금이 아직 안 된 청구서 수 — 사이드바 배지용.
   * 화면을 넘길 때마다 불리므로 목록이 아니라 개수만 받는다.
   * 실패해도 화면이 깨지면 안 되므로 호출부에서 0으로 넘긴다.
   */
  async getUnpaidBillCount(): Promise<number> {
    const result = await this.apiCall<{ count: number }>(
      '/api/v1/owner/billing/alimtalk-bills/unpaid-count',
    );
    return result.count || 0;
  }

  /**
   * 과거 청구 내역 조회
   */
  async getBillingHistory(months = 6): Promise<MonthlyBill[]> {
    const result = await this.apiCall<{
      history: MonthlyBill[];
    }>(`/api/v1/owner/billing/history?months=${months}`);
    return result.history;
  }

  /**
   * 추가 비용 조회
   */
  async getCharges(yearMonth?: string): Promise<MonthlyCharge[]> {
    const url = yearMonth
      ? `/api/v1/owner/billing/charges?year_month=${yearMonth}`
      : '/api/v1/owner/billing/charges';

    const result = await this.apiCall<{
      charges: MonthlyCharge[];
    }>(url);
    return result.charges;
  }

  /**
   * 최근 실패한 알림 로그 조회 (error_message 포함)
   */
  async getRecentFailedLogs(days: number = 30, limit: number = 20): Promise<FailedNotificationLog[]> {
    const result = await this.apiCall<{
      logs: FailedNotificationLog[];
      total: number;
    }>(`/api/v1/owner/notification-logs?days=${days}&limit=${limit}`);
    return (result.logs || []).filter(log => log.status === 'failed');
  }

  // ============================================================================
  // 크레딧 관련 메서드
  // ============================================================================

  /**
   * 크레딧 잔액 조회
   */
  async getCreditBalance(): Promise<CreditBalance> {
    const result = await this.apiCall<{
      success: boolean;
      credit: CreditBalance;
    }>('/api/v1/owner/credits/balance');
    return result.credit;
  }

  /**
   * 크레딧 거래 내역 조회
   */
  async getCreditTransactions(limit = 50, offset = 0): Promise<CreditTransaction[]> {
    const result = await this.apiCall<{
      success: boolean;
      transactions: CreditTransaction[];
      count: number;
    }>(`/api/v1/owner/credits/transactions?limit=${limit}&offset=${offset}`);
    return result.transactions;
  }

  /**
   * 알림톡 발송 가능 여부 체크
   */
  async canSendAlimtalk(): Promise<{ can_send: boolean; reason?: string }> {
    const result = await this.apiCall<{
      success: boolean;
      can_send: boolean;
      reason?: string;
    }>('/api/v1/owner/credits/can-send');
    return { can_send: result.can_send, reason: result.reason };
  }

  /**
   * 크레딧 충전 요청 (계좌이체 등)
   */
  async requestCharge(amount: number, paymentMethod: string, notes?: string): Promise<{ success: boolean; request_id?: string }> {
    const result = await this.apiCall<{
      success: boolean;
      request_id?: string;
    }>('/api/v1/owner/credits/request-charge', {
      method: 'POST',
      body: JSON.stringify({ amount, payment_method: paymentMethod, notes }),
    });
    return result;
  }

  // ============================================================================
  // 결제/미납금 관련 메서드
  // ============================================================================

  /**
   * 특정 월의 결제 요약 조회
   */
  async getPaymentSummary(yearMonth: string): Promise<PaymentSummary> {
    const result = await this.apiCall<{
      success: boolean;
    } & PaymentSummary>(`/api/v1/owner/billing/payment-summary/${yearMonth}`);
    return {
      year_month: result.year_month,
      total_billed: result.total_billed,
      total_paid: result.total_paid,
      outstanding: result.outstanding,
      is_paid: result.is_paid,
      payments: result.payments,
    };
  }

  /**
   * 전체 미납금 조회
   */
  async getOutstandingBalance(): Promise<OutstandingBalance> {
    const result = await this.apiCall<{
      success: boolean;
    } & OutstandingBalance>('/api/v1/owner/billing/outstanding');
    return {
      total_outstanding: result.total_outstanding,
      months_with_outstanding: result.months_with_outstanding,
    };
  }

  /**
   * 결제 내역 조회
   */
  async getPayments(yearMonth?: string): Promise<PaymentRecord[]> {
    const url = yearMonth
      ? `/api/v1/owner/billing/payments?year_month=${yearMonth}`
      : '/api/v1/owner/billing/payments';

    const result = await this.apiCall<{
      success: boolean;
      payments: PaymentRecord[];
    }>(url);
    return result.payments;
  }
}

export const billingUsageRepository = new BillingUsageRepository();
